/**
 * Thematic colours for interest cluster families (tiles, modals, illustrations).
 * Neutral surfaces + family ink/accent follow styles/theme.css (light/dark).
 * Tile classes use `dark:` variants so pastel cards adapt in dark mode.
 * @typedef {{ gradient: string, cta: string, ctaHover: string, accent: string, surface: string, ink: string, inkMuted: string, badge: string, tile: { card: string, badge: string, hint: string, hover: string } }} InterestFamilyTheme
 */

/** Neutral panel fill — follows styles/theme.css light/dark tokens. */
const THEME_SURFACE = 'rgb(var(--edvise-surface))';

/**
 * @param {string} family
 * @returns {{ ink: string, inkMuted: string, accent: string }}
 */
function familyInkTokens(family) {
  return {
    ink: `rgb(var(--interest-${family}-ink))`,
    inkMuted: `rgb(var(--interest-${family}-ink-muted))`,
    accent: `rgb(var(--interest-${family}-accent))`
  };
}

/** @type {Record<string, InterestFamilyTheme>} */
export const INTEREST_FAMILY_THEMES = {
  business: {
    gradient: 'from-slate-700 via-slate-800 to-blue-900',
    cta: 'bg-gradient-to-r from-slate-700 via-slate-800 to-blue-900',
    ctaHover: 'hover:from-slate-600 hover:via-slate-700 hover:to-blue-800',
    ...familyInkTokens('business'),
    surface: THEME_SURFACE,
    badge: 'bg-white/20 text-white',
    tile: {
      card: 'bg-blue-50/80 text-gray-900 border-blue-200/80 dark:bg-blue-950/50 dark:text-blue-50 dark:border-blue-800/60',
      badge: 'bg-blue-700 text-white dark:bg-blue-500',
      hint: 'text-blue-900/70 dark:text-blue-200/80',
      hover: 'hover:bg-blue-100/80 hover:border-blue-300/80 dark:hover:bg-blue-900/45 dark:hover:border-blue-700/60'
    }
  },
  technology: {
    gradient: 'from-indigo-600 via-violet-600 to-purple-800',
    cta: 'bg-gradient-to-r from-indigo-600 via-violet-600 to-purple-800',
    ctaHover: 'hover:from-indigo-500 hover:via-violet-500 hover:to-purple-700',
    ...familyInkTokens('technology'),
    surface: THEME_SURFACE,
    badge: 'bg-white/20 text-white',
    tile: {
      card: 'bg-violet-50/80 text-gray-900 border-violet-200/80 dark:bg-violet-950/50 dark:text-violet-50 dark:border-violet-800/60',
      badge: 'bg-violet-600 text-white dark:bg-violet-500',
      hint: 'text-violet-900/70 dark:text-violet-200/80',
      hover: 'hover:bg-violet-100/80 hover:border-violet-300/80 dark:hover:bg-violet-900/45 dark:hover:border-violet-700/60'
    }
  },
  engineering: {
    gradient: 'from-teal-500 via-cyan-600 to-slate-700',
    cta: 'bg-gradient-to-r from-teal-600 via-cyan-600 to-slate-700',
    ctaHover: 'hover:from-teal-500 hover:via-cyan-500 hover:to-slate-600',
    ...familyInkTokens('engineering'),
    surface: THEME_SURFACE,
    badge: 'bg-teal-600 text-white dark:bg-teal-500',
    tile: {
      card: 'bg-cyan-50/75 text-gray-900 border-cyan-200/80 dark:bg-cyan-950/50 dark:text-cyan-50 dark:border-cyan-800/60',
      badge: 'bg-cyan-700 text-white dark:bg-cyan-500',
      hint: 'text-cyan-900/70 dark:text-cyan-200/80',
      hover: 'hover:bg-cyan-100/80 hover:border-cyan-300/80 dark:hover:bg-cyan-900/45 dark:hover:border-cyan-700/60'
    }
  },
  creative: {
    gradient: 'from-rose-400 via-pink-500 to-fuchsia-600',
    cta: 'bg-gradient-to-r from-rose-500 via-pink-500 to-fuchsia-600',
    ctaHover: 'hover:from-rose-400 hover:via-pink-400 hover:to-fuchsia-500',
    ...familyInkTokens('creative'),
    surface: THEME_SURFACE,
    badge: 'bg-rose-500 text-white',
    tile: {
      card: 'bg-rose-50/80 text-gray-900 border-rose-200/80 dark:bg-rose-950/50 dark:text-rose-50 dark:border-rose-800/60',
      badge: 'bg-rose-500 text-white dark:bg-rose-400 dark:text-rose-950',
      hint: 'text-rose-900/70 dark:text-rose-200/80',
      hover: 'hover:bg-rose-100/80 hover:border-rose-300/80 dark:hover:bg-rose-900/45 dark:hover:border-rose-700/60'
    }
  },
  humanities: {
    gradient: 'from-amber-400 via-orange-500 to-yellow-600',
    cta: 'bg-gradient-to-r from-amber-500 via-orange-500 to-yellow-600',
    ctaHover: 'hover:from-amber-400 hover:via-orange-400 hover:to-yellow-500',
    ...familyInkTokens('humanities'),
    surface: THEME_SURFACE,
    badge: 'bg-amber-600 text-white dark:bg-amber-500',
    tile: {
      card: 'bg-amber-50/75 text-gray-900 border-amber-200/80 dark:bg-amber-950/45 dark:text-amber-50 dark:border-amber-800/60',
      badge: 'bg-amber-600 text-white dark:bg-amber-500',
      hint: 'text-amber-950/70 dark:text-amber-200/80',
      hover: 'hover:bg-amber-100/80 hover:border-amber-300/80 dark:hover:bg-amber-900/40 dark:hover:border-amber-700/60'
    }
  },
  social: {
    gradient: 'from-emerald-500 via-green-600 to-teal-700',
    cta: 'bg-gradient-to-r from-emerald-600 via-green-600 to-teal-700',
    ctaHover: 'hover:from-emerald-500 hover:via-green-500 hover:to-teal-600',
    ...familyInkTokens('social'),
    surface: THEME_SURFACE,
    badge: 'bg-emerald-600 text-white dark:bg-emerald-500',
    tile: {
      card: 'bg-emerald-50/75 text-gray-900 border-emerald-200/80 dark:bg-emerald-950/50 dark:text-emerald-50 dark:border-emerald-800/60',
      badge: 'bg-emerald-600 text-white dark:bg-emerald-500',
      hint: 'text-emerald-900/70 dark:text-emerald-200/80',
      hover: 'hover:bg-emerald-100/80 hover:border-emerald-300/80 dark:hover:bg-emerald-900/45 dark:hover:border-emerald-700/60'
    }
  },
  health: {
    gradient: 'from-red-400 via-rose-500 to-pink-600',
    cta: 'bg-gradient-to-r from-red-500 via-rose-500 to-pink-600',
    ctaHover: 'hover:from-red-400 hover:via-rose-400 hover:to-pink-500',
    ...familyInkTokens('health'),
    surface: THEME_SURFACE,
    badge: 'bg-red-500 text-white dark:bg-red-400 dark:text-red-950',
    tile: {
      card: 'bg-red-50/75 text-gray-900 border-red-200/80 dark:bg-red-950/50 dark:text-red-50 dark:border-red-800/60',
      badge: 'bg-red-500 text-white dark:bg-red-400 dark:text-red-950',
      hint: 'text-red-900/70 dark:text-red-200/80',
      hover: 'hover:bg-red-100/80 hover:border-red-300/80 dark:hover:bg-red-900/45 dark:hover:border-red-700/60'
    }
  },
  general: {
    gradient: 'from-slate-500 via-gray-600 to-zinc-700',
    cta: 'bg-gradient-to-r from-slate-600 via-gray-600 to-zinc-700',
    ctaHover: 'hover:from-slate-500 hover:via-gray-500 hover:to-zinc-600',
    ...familyInkTokens('general'),
    surface: THEME_SURFACE,
    badge: 'bg-slate-600 text-white dark:bg-slate-500',
    tile: {
      card: 'bg-slate-50 text-gray-900 border-slate-200 dark:bg-slate-900/70 dark:text-slate-50 dark:border-slate-700',
      badge: 'bg-slate-600 text-white dark:bg-slate-500',
      hint: 'text-slate-700/80 dark:text-slate-300/80',
      hover: 'hover:bg-slate-100 hover:border-slate-300 dark:hover:bg-slate-800/80 dark:hover:border-slate-600'
    }
  }
};

const DEFAULT_THEME = INTEREST_FAMILY_THEMES.general;

/*
 * Colour is deliberately independent from academic family. This keeps adjacent
 * results visually distinct even when several top matches share one family.
 */
const INTEREST_CLUSTER_THEME_KEYS = {
  0: 'social',
  1: 'social',
  2: 'engineering',
  3: 'humanities',
  4: 'health',
  5: 'creative',
  6: 'humanities',
  7: 'creative',
  8: 'technology',
  9: 'humanities',
  10: 'health',
  11: 'creative',
  12: 'humanities',
  13: 'business',
  14: 'humanities',
  15: 'technology',
  16: 'business',
  17: 'engineering',
  18: 'health',
  19: 'humanities',
  20: 'social',
  21: 'engineering',
  22: 'technology',
  23: 'health',
  24: 'technology',
  25: 'humanities',
  26: 'health',
  27: 'social',
  28: 'social',
  29: 'creative',
  30: 'creative',
  31: 'engineering',
  32: 'engineering',
  33: 'technology',
  34: 'business'
};

/**
 * @param {string} family
 * @returns {InterestFamilyTheme}
 */
export function getInterestFamilyTheme(family) {
  return INTEREST_FAMILY_THEMES[family] || DEFAULT_THEME;
}

/**
 * Stable visual theme for an individual cluster, falling back to its family.
 * @param {{ cluster_id?: number }} cluster
 * @param {string} family
 */
export function getInterestClusterTheme(cluster, family) {
  const id = Number(cluster?.cluster_id);
  const themeKey = Number.isFinite(id) ? INTEREST_CLUSTER_THEME_KEYS[id] : null;
  return getInterestFamilyTheme(themeKey || family);
}

/**
 * Flat tile palette for grid cards (modals keep gradient headers).
 * @param {string} family
 */
export function getInterestClusterTileStyle(cluster, family) {
  return getInterestClusterTheme(cluster, family).tile;
}
